/* eslint-disable */
import { AlertQuestionIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { ChipMetadata, LinkButton, Tooltip } from '@pmi/dsm-react';
import DateRangePicker from '../../../shared/components/DateRangePicker';
import Overlay from '../../../shared/components/Overlay';
import { ArrowBackIcon } from '@pmi/dsm-react/dist/components/icons/fa/24';
import { useNavigate } from 'react-router-dom';
import styles from './productDetailsReport.module.scss';
import '../customDateRange.scss';
import parentStyles from '../report.module.scss';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import { useGetProductDetailsQuery } from './apiSlice';
import CircleChart from './DonutChart/CircleChart';
import formatCurrency from '../../../shared/utils/formatCurrency';
import { PMIFormatDate } from '../../../shared/helpers';
import ReportTypeTab from '../ReportTypeTab';
import hasCustomDateRange from '../hasCustomDateRange';
import dateParam from '../dateParam';
import useGetDeals from '../../../shared/hooks/useGetDeals';
import DealTab from '../../../shared/components/DealTab';
import PERMISSIONS from '../../../shared/permissions';
import usePermissions from '../../../shared/hooks/usePermissions';
import StatusBreakdown from './StatusBreakdown';
import RegisteredUsers from './RegisteredUsers';
import { getBundleItems } from '../services';
import { BundleReportItem } from '../../../shared/models/ReportModel';
import { DateRangeType } from '@pmi/dsm-react/dist/components/date-time/types';
import useAdminVisitedPages from '../../../shared/hooks/useAdminVisitedPages';
import { DealOverview } from '../../../shared/models/DealModel';
import UnavailableChipMetadata from '../../../shared/components/UnavailableChipMetadata';
import { isPayByUser, ProductTypes } from '../../../shared/constants';
import AdminPayByUserNotification from '../../../shared/components/AdminPayByUserNotification';

export interface IStatusCount {
  label: string;
  value: number | string;
  pre?: string;
  over?: number;
  tooltip?: string;
}

export interface IRegisteredUser {
  name: string;
  status: string;
  id: string;
}

export default function ProductDetailsReport() {
  const { dealId, productId } = useParams();
  const { currentAccountId } = useSelector(accountSelector);
  const { hasAccess, isLoading: isPermLoading } = usePermissions();
  const {
    data: dealList,
    isLoading: isDealListLoading,
    isFetching: isDealListFetching,
    selectedHomeDeal: selectedDeal,
    setSelectedHomeDeal: setSelectedDeal,
  } = useGetDeals({
    allDealsEnabled: hasAccess({
      permissions: [PERMISSIONS.ReportingAllAgreementsAccess],
    }),
  });

  const filteredDealList = useMemo(() => {
    return dealList
      .filter((d) => d.products.findIndex((p) => p.sku === productId) >= 0)
      .map(
        (d) =>
          ({
            ...d,
            ...{ accountId: currentAccountId },
          } as DealOverview),
      );
  }, [dealList]);

  const [filterValue, setFilterValue] = useState<string[]>([]);
  const { dateRange } = useParams();
  const [reportType, setReportType] = useState(dateRange || 'All Time');
  const [startDate, setStartDate] = useState(
    hasCustomDateRange(reportType)
      ? new Date(reportType.split('-')[0].split(':')[1])
      : undefined,
  );
  const [endDate, setEndDate] = useState(
    hasCustomDateRange(reportType)
      ? new Date(reportType.split('-')[1])
      : undefined,
  );
  const [selectedProductTypeTab, setSelectedProductTypeTab] =
    useState<string>(null);

  const startDateParam = dateParam({
    reportType,
    type: 'start',
    startDate,
    endDate,
  });
  const endDateParam = dateParam({
    reportType,
    type: 'end',
    startDate,
    endDate,
  });
  const location = useLocation();

  const {
    data: productDetails,
    isLoading,
    isFetching,
  } = useGetProductDetailsQuery(
    {
      accountId: currentAccountId,
      dealId: selectedDeal?.id,
      sku: productId,
      StartDate: startDateParam,
      EndDate: endDateParam,
    },
    { skip: !currentAccountId || !selectedDeal },
  );

  const statusCount = useMemo(
    () =>
      productDetails?.product
        ? [
            {
              label: 'Total Spent',
              value: formatCurrency(productDetails.product.totalSpend),
              pre: '$',
              tooltip:
                'The monetary amount charged to your organization for this product (Price x Quantity).',
            },
            {
              label: 'Redeemed',
              value: productDetails.product.quantityRedeemed,
              tooltip: 'Total redeems for this product.',
            },
          ]
        : [],
    [productDetails],
  );

  const redeemedPercent = useMemo(
    () =>
      productDetails?.product
        ? Math.round(
            (productDetails.product.quantityRedeemed /
              productDetails.product.quantityTotal) *
              100,
          )
        : 0,
    [productDetails],
  );

  const usersTableRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (range: DateRangeType) => {
    if (!range.startDate || !range.endDate) {
      return;
    }

    const startDate = new Date(range.startDate);
    const endDate = new Date(range.endDate);
    setStartDate(startDate);
    setEndDate(endDate);

    if (startDate && endDate) {
      window.history.replaceState(
        null,
        '',
        `${location.pathname.split(':')[0]}: ${PMIFormatDate(
          startDate.toDateString(),
        )} - ${PMIFormatDate(endDate.toDateString())}`,
      );
      setReportType(
        `Custom Date Range: ${PMIFormatDate(
          startDate.toDateString(),
        )} - ${PMIFormatDate(endDate.toDateString())}`,
      );
    }
  };

  let typeLabel = productDetails?.product?.type;
  // handle dynamic text in registered user table section
  let tableLabel = 'Redeems in';
  let productTypeLabel = '';

  switch (typeLabel) {
    case 'Certification':
      productTypeLabel = 'certification';
      tableLabel = `${tableLabel} this ${productTypeLabel}`;
      break;
    case ProductTypes.membership:
      productTypeLabel = 'membership';
      tableLabel = `${tableLabel} ${productTypeLabel}`;
      break;
    case 'Chapter Membership':
      productTypeLabel = 'chapter membership';
      tableLabel = `${tableLabel} ${productTypeLabel}`;
      break;
    case 'eLearning':
      typeLabel = 'Course';
      productTypeLabel = 'course';
      tableLabel = `${tableLabel} this ${productTypeLabel}`;
      break;
    case 'Digital Product':
      productTypeLabel = 'product';
      tableLabel = `${tableLabel} this ${productTypeLabel}`;
      break;
    default:
      break;
  }

  const scrollToTable = (value: string) => {
    setFilterValue([value]);
    setTimeout(() => {
      usersTableRef.current.scrollIntoView();
    }, 300);
  };

  useEffect(() => {
    const splitValue = location.pathname.split('/');
    const updatedUrl = splitValue.slice(0, -1).concat([reportType]).join('/');
    // force change the url based from the reportTypeSelection
    window.history.replaceState(null, '', updatedUrl);
  }, [reportType]);

  useEffect(() => {
    if (dealList && !isDealListLoading && !isDealListFetching && selectedDeal) {
      navigate(
        `/admin/${currentAccountId}/reports/deal/${selectedDeal.id}/sku/${productId}/${reportType}`,
      );
    }
  }, [
    isDealListLoading,
    isDealListFetching,
    dealList,
    selectedDeal,
    reportType,
  ]);

  const handleProductTypeTabChange = (sku: string) => {
    setSelectedProductTypeTab(sku);
    setFilterValue([]);
  };

  useEffect(() => {
    if (productDetails?.product) {
      const items: BundleReportItem[] = productDetails.product.bundle
        ? getBundleItems(productDetails.product)
        : [productDetails.product];
      if (!items.find((e) => e.sku === selectedProductTypeTab))
        setSelectedProductTypeTab(items[0].sku);
    }
  }, [productDetails]);

  const visits = useAdminVisitedPages('reports.details');

  useEffect(() => {
    if (!isDealListLoading && !isPermLoading && !visits.isLoading) {
      visits.performFirstTimeVisit('.report_details-registered_users-table');
    }
  }, [isDealListLoading, isPermLoading, visits.isLoading]);
  return (
    <>
      {selectedDeal && filteredDealList.length > 1 && (
        <div className="mb-4">
          <DealTab
            onTabChange={(deal) => {
              setSelectedDeal(deal);
            }}
            selectedDeal={selectedDeal}
            deals={filteredDealList}
            showAllSelector={hasAccess({
              permissions: [PERMISSIONS.ReportingAllAgreementsAccess],
            })}
          />
        </div>
      )}

      <div className={`box ${styles['product-details-report']}`}>
        {selectedDeal && productDetails ? (
          <>
            <div className="d-flex align-items-end">
              <h5 className="fw-normal fs-4 mb-0 pe-3">
                <strong className="fw-bold">
                  {productDetails.product.retired && (
                    <UnavailableChipMetadata />
                  )}
                  <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: productDetails.product.name,
                    }}
                    data-testid="product-name"
                  />
                </strong>
              </h5>
              <span data-testid="product-sku">
                <ChipMetadata
                  text={`SKU: ${productDetails.product.sku}`}
                  variant="violet"
                />
              </span>
            </div>

            {filteredDealList.length === 1 && (
              <span className={`label ${styles['grey-5']}`}>
                Agreement ID: {filteredDealList[0].id}
              </span>
            )}

            <div className="mt-2 mb-3">
              <LinkButton
                iconAlign="left"
                variant="light"
                titleText="Back to overview"
                icon={ArrowBackIcon}
                className="border back-to-overview"
                onClick={() => {
                  navigate(
                    `/admin/${currentAccountId}/reports/${dealId}/${reportType}`,
                  );
                }}
              />
            </div>
            <AdminPayByUserNotification agreementId={selectedDeal.id} />
            <div className="report_details-date_tabs mb-3">
              <ReportTypeTab
                reportType={reportType}
                onTabChange={setReportType}
                tabChange={() => {}}
                startDate={
                  startDate
                    ? PMIFormatDate(new Date(startDate).toDateString())
                    : undefined
                }
                endDate={
                  endDate
                    ? PMIFormatDate(new Date(endDate).toDateString())
                    : undefined
                }
              />
              {reportType.includes('Custom Date Range') && (
                <div className="customDateRange mt-3">
                  <DateRangePicker
                    className={parentStyles.customDateRange}
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
            <div
              className={`d-md-flex d-block mt-3 ${
                isPayByUser(selectedDeal.paymentArrangement)
                  ? 'flex-column'
                  : ''
              }`}
            >
              <div className="w-100 w-md-50">
                {/* TOTAL */}
                <div
                  className={`d-md-flex justify-content-between d-block mb-0 mb-md-4 ${
                    isPayByUser(selectedDeal.paymentArrangement) ? '' : 'me-4'
                  }`}
                >
                  {statusCount.slice(0, 2).map((card, index) => (
                    <div
                      key={card.label}
                      className={`w-100 w-md-50 mb-4 mb-md-0 mx-auto ${
                        index === 0 ? 'me-4' : ''
                      }`}
                    >
                      <div className={styles['card-container']}>
                        <div className="p-3">
                          <div
                            className={`label ${styles['grey-5']} d-flex align-items-center`}
                          >
                            {card.label}{' '}
                            <Tooltip
                              type="sm"
                              content={card.tooltip}
                              placement="right"
                              className="ps-0 pe-0 darkTooltip"
                            >
                              <AlertQuestionIcon
                                fontSize="sm"
                                className="ms-1"
                              />
                            </Tooltip>
                          </div>
                          <div>
                            <h5 className=" fw-bold fs-3">
                              {card.pre}
                              {card.value}
                            </h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* GRAPH */}
                {!isPayByUser(selectedDeal.paymentArrangement) && (
                  <div
                    className={`${styles['card-container']} mb-4 mb-md-0 me-0 me-md-4`}
                  >
                    <div className="p-3">
                      <h5 className=" fw-bold fs-5 mb-0">
                        Product{' '}
                        {productDetails.product.retired
                          ? 'history'
                          : 'availability'}
                      </h5>
                      <span
                        className={`label ${styles['grey-5']}`}
                        data-testid="product-redeemed"
                      >
                        {redeemedPercent}% have been redeemed{' '}
                      </span>
                      <div className="d-flex justify-content-center p-2">
                        <CircleChart
                          retired={productDetails.product.retired}
                          current={productDetails.product.quantityRedeemed}
                          remaining={
                            !productDetails.product.retired
                              ? productDetails.product.quantityNotRedeemed
                              : 0
                          }
                          required={productDetails.product.quantityTotal}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <StatusBreakdown
                product={productDetails.product}
                scrollToTable={scrollToTable}
                onBundleProductTabChange={handleProductTypeTabChange}
                selectedProductTypeTab={selectedProductTypeTab}
              />
            </div>
            <div className="report_details-registered_users-table">
              <RegisteredUsers
                dealId={dealId}
                endDate={endDateParam}
                filterValue={filterValue}
                product={productDetails.product}
                productId={productId}
                startDate={startDateParam}
                usersTableRef={usersTableRef}
                reportType={reportType}
                onBundleProductTabChange={handleProductTypeTabChange}
                selectedProductTypeTab={selectedProductTypeTab}
              />
            </div>
          </>
        ) : null}
        <Overlay
          variant="PMIOverlay"
          global
          animated
          fade
          iconColor="brand-dark"
          overlayColor="black"
          overlayOpacity={1}
          show={
            isLoading ||
            isFetching ||
            isDealListFetching ||
            isDealListLoading ||
            isPermLoading ||
            visits.isLoading
          }
        />
      </div>
    </>
  );
}
